import type { TemplateContext } from "@unbox/api";
import { InfoCircle } from "@untitledui/icons";
import { useEffect, useState } from "react";
import { useCreators } from "@/api/creator";
import { useStoreComposerImage } from "@/api/message-images";
import {
  useCreateMessageTemplate,
  useMessageTemplateCategories,
  useMessageTemplates,
  useUpdateMessageTemplate,
} from "@/api/message-templates";
import { useProducts } from "@/api/products";
import { Button } from "@/components/base/buttons/button";
import {
  type ComposerImage,
  composerImageFromStored,
  releaseComposerImage,
} from "@/components/message-composer/composer-image";
import { SAMPLE_PREVIEW_CONTEXT } from "@/components/message-composer/composer-preview";
import {
  toastTemplateNotSaved,
  toastTemplateSaved,
  toastTemplateUpdated,
} from "@/components/message-composer/composer-toasts";
import { ComposerPreviewButton, MessageComposer } from "@/components/message-composer/message-composer";
import {
  DEFAULT_TEMPLATE_CATEGORY,
  TemplateDetailsFields,
  TemplateDialogHeader,
} from "@/components/message-composer/template-details";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useMerchantName } from "@/hooks/use-merchant-name";
import { formatCreatorIdShort } from "@/utils/formatters";

function pickRandom<T>(arr: T[]): T | undefined {
  if (!arr.length) return undefined;
  return arr[Math.floor(Math.random() * arr.length)];
}

interface TemplateEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId?: string | null;
  onCreated?: () => void;
}

export function TemplateEditorDialog({ open, onOpenChange, templateId, onCreated }: TemplateEditorDialogProps) {
  const isNew = !templateId;
  const merchantName = useMerchantName();
  const { data: templates = [] } = useMessageTemplates();
  const { data: creators = [] } = useCreators();
  const { data: products = [] } = useProducts();
  const { mutateAsync: createTemplate, isPending: isCreating } = useCreateMessageTemplate();
  const { mutateAsync: updateTemplate, isPending: isUpdating } = useUpdateMessageTemplate();
  const { store: storeImage, isPending: isStoringImage } = useStoreComposerImage();

  const [content, setContent] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [image, setImage] = useState<ComposerImage | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContext, setPreviewContext] = useState<TemplateContext>(SAMPLE_PREVIEW_CONTEXT);
  const isPending = isCreating || isUpdating || isStoringImage;

  const categories = useMessageTemplateCategories();

  useEffect(() => {
    if (!open) return;
    const template = templateId ? templates.find((t) => t.id === templateId) : undefined;
    setName(template?.name ?? "");
    setCategory(template?.category ?? "");
    setContent(template?.content ?? "");
    setImage(
      template?.imageId && template.imageUrl ? composerImageFromStored(template.imageId, template.imageUrl) : null,
    );
    setPreviewOpen(false);
  }, [open, templateId, templates]);

  const buildPreviewContext = (): TemplateContext => {
    const creator = pickRandom(creators as { creatorId: string }[]);
    const product = pickRandom(products as unknown as { name: string }[]);
    const shopName = merchantName;
    return {
      ...SAMPLE_PREVIEW_CONTEXT,
      ...(creator && { creatorName: formatCreatorIdShort(creator.creatorId) }),
      ...(shopName && { shopName }),
      ...(product && { productNameList: [product.name] }),
    };
  };

  const togglePreview = () => {
    if (!previewOpen) setPreviewContext(buildPreviewContext());
    setPreviewOpen(!previewOpen);
  };

  const close = () => {
    releaseComposerImage(image);
    onOpenChange(false);
  };

  const handleSave = async () => {
    const input = {
      name: name.trim(),
      category: category.trim() || DEFAULT_TEMPLATE_CATEGORY,
      content: content.trim(),
    };
    try {
      const imageId = await storeImage(image);
      if (isNew) {
        await createTemplate({ ...input, imageId });
        toastTemplateSaved(input.name, input.category);
        onCreated?.();
      } else {
        await updateTemplate({ id: templateId, input: { ...input, imageId } });
        toastTemplateUpdated(input.name);
      }
      releaseComposerImage(image);
      onOpenChange(false);
    } catch {
      toastTemplateNotSaved();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent
        hideCloseButton
        aria-describedby={undefined}
        className="max-h-[85vh] gap-5 overflow-y-auto rounded-2xl border-none bg-primary p-7 sm:max-w-[720px] sm:rounded-2xl"
      >
        <TemplateDialogHeader title={isNew ? "New Template" : "Edit Template"} onClose={close} />

        <MessageComposer
          value={content}
          onChange={setContent}
          image={image}
          onImageChange={setImage}
          variableContext="all"
          templates={false}
          showActions={false}
          preview={previewOpen}
          onPreviewChange={setPreviewOpen}
          previewContext={previewContext}
          placeholder="Type your message, or use the tools below to add variables and images…"
          minHeight={200}
          textClassName="p-3.5 text-base leading-6"
          aria-label="Template message"
        />

        <div className="flex flex-wrap items-end gap-4">
          <TemplateDetailsFields
            name={name}
            onNameChange={setName}
            category={category}
            onCategoryChange={setCategory}
            categories={categories}
            nameLabel="Template Name"
            labelClassName="text-base font-semibold text-primary"
            fieldClassName="min-w-[200px] flex-1"
          />
          <ComposerPreviewButton
            open={previewOpen && !!content.trim()}
            isDisabled={!content.trim()}
            onToggle={togglePreview}
            className="shrink-0"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="inline-flex items-center gap-2 text-sm text-quaternary">
            <InfoCircle aria-hidden className="size-4" />
            Messages sent securely
          </p>
          <div className="flex gap-3">
            <Button color="secondary" onClick={close} isDisabled={isPending}>
              Cancel
            </Button>
            <Button isDisabled={!content.trim() || !name.trim()} isLoading={isPending} onClick={handleSave}>
              Save Template
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
